import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { MESSENGER_MESSAGE_TAGS } from "../../constants/messengerMessageTags";
import { INSTAGRAM_MESSAGE_TAGS } from "../../constants/instagramMessageTags";
import { isInstagramDmChannel } from "../../utils/serviceWindow";

const useStyles = makeStyles((theme) => ({
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    paddingRight: theme.spacing(5),
    position: "relative",
  },
  titleIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 193, 7, 0.12)",
    color: "#FFC107",
    flexShrink: 0,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
  },
  closeButton: {
    position: "absolute",
    right: 0,
    top: 0,
  },
  formField: {
    marginTop: theme.spacing(2),
  },
  policyAlert: {
    marginTop: theme.spacing(2),
  },
}));

const MessengerTagSendModal = ({
  open,
  onClose,
  ticketId,
  channel,
  onOptimisticMessage,
  senderName,
  signMessage = true,
}) => {
  const classes = useStyles();
  const availableTags = isInstagramDmChannel(channel)
    ? INSTAGRAM_MESSAGE_TAGS
    : MESSENGER_MESSAGE_TAGS;
  const [messageTag, setMessageTag] = useState(availableTags[0]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setBody("");
    setMessageTag(availableTags[0]);
    onClose();
  };

  const handleSend = async () => {
    const trimmed = body.trim();
    if (!trimmed || !messageTag) {
      toast.error(i18n.t("messengerTags.validation.bodyRequired"));
      return;
    }

    const tempId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    const finalBody = signMessage && senderName
      ? `*${senderName}:*\n${trimmed}`
      : trimmed;

    if (typeof onOptimisticMessage === "function") {
      onOptimisticMessage({
        id: tempId,
        ticketId,
        body: finalBody,
        fromMe: true,
        mediaUrl: "",
        mediaType: null,
        quotedMsgId: null,
        isWhisper: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        ack: 0,
        status: "pending",
        _optimistic: true,
        tempId,
      });
    }

    setLoading(true);
    try {
      await api.post(`/messages/${ticketId}`, {
        body: finalBody,
        fromMe: true,
        read: 1,
        tempId,
        messageTag,
      });
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <div className={classes.titleRow}>
          <div className={classes.titleIcon}>
            <LabelOutlinedIcon />
          </div>
          <div>
            <Typography variant="h6">
              {i18n.t("messengerTags.modal.title")}
            </Typography>
            <Typography variant="body2" className={classes.subtitle}>
              {i18n.t("messengerTags.modal.subtitle")}
            </Typography>
          </div>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" className={classes.policyAlert}>
          {i18n.t(
            isInstagramDmChannel(channel)
              ? "messengerTags.modal.policyHintInstagram"
              : "messengerTags.modal.policyHint",
          )}
        </Alert>
        {availableTags.length > 1 ? (
          <FormControl variant="outlined" fullWidth className={classes.formField}>
            <InputLabel id="messenger-tag-label">
              {i18n.t("messengerTags.modal.tagLabel")}
            </InputLabel>
            <Select
              labelId="messenger-tag-label"
              value={messageTag}
              onChange={(e) => setMessageTag(e.target.value)}
              label={i18n.t("messengerTags.modal.tagLabel")}
            >
              {availableTags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {i18n.t(`messengerTags.tags.${tag}.label`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography variant="body2" className={classes.formField}>
            {i18n.t(`messengerTags.tags.${messageTag}.label`)}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
          {i18n.t(`messengerTags.tags.${messageTag}.hint`)}
        </Typography>
        <TextField
          className={classes.formField}
          label={i18n.t("messengerTags.modal.messageLabel")}
          placeholder={i18n.t("messengerTags.modal.messagePlaceholder")}
          multiline
          minRows={4}
          variant="outlined"
          fullWidth
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {i18n.t("messengerTags.modal.cancel")}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disableElevation
          startIcon={<SendIcon />}
          onClick={handleSend}
          disabled={loading || !body.trim()}
        >
          {i18n.t("messengerTags.modal.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessengerTagSendModal;
